import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import SkeletonGroup from 'components/Skeleton/SkeletonGroup';

import useCommentIdFromRouterHash from 'hooks/useCommentIdFromRouterHash';
import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import useMediaQuery from 'hooks/useMediaQuery';
import useRatings from 'hooks/useRatings';

import { nonNull, stripObjDuplicates } from 'lib/array';
import { getCommentsCount } from 'lib/comments';
import { sendEvent } from 'lib/gtag';
import { commentQueryKeys } from 'lib/queryKeys';
import { scrollToElement } from 'lib/utils';

import { useDomain } from 'providers/DomainProvider';

import { useCommentsRoot } from '../Root';

import COMMENT from 'constants/comment';

const CommentListItem = dynamic(() => import('./ListItem'));

const CommentListItemSkeleton = dynamic(() => import('./ListItem/Skeleton'), {
  ssr: false,
});

const COMMENTS_STALE_TIME = 20_000; // 20 seconds
const MILLISECONDS_TO_WAIT_BEFORE_SCROLLING_TO_COMMENT = 500;

const CommentsList = ({
  initialComments,
  canCommentsBeRecommendedAsReview = false,
  className,
  commentsCount: initialCommentsCount,
  queryFn,
}) => {
  const hashCommentRef = useRef(null);
  const commentsListComponentRef = useRef(null);
  const lastCommentIdFromUrlHash = useRef(null);
  const fetchNextPageRef = useRef(null);
  const { isLg } = useMediaQuery();
  const { commentId: commentIdFromUrlHash } = useCommentIdFromRouterHash();
  const { domain, domainId, domainIdKey } = useDomain();
  const { isLiked, isDisliked } = useRatings({ domain, domainId });
  const {
    commentsSort,
    gaEventCategory,
    dispatchCommentsCount,
    setCommentsListEmpty,
  } = useCommentsRoot();

  const {
    data: comments,
    fetchNextPage,
    isLoading,
    isSuccess,
    isFetchingNextPage,
    isInitialLoading,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: commentQueryKeys[domain].list({
      [domainIdKey]: domainId,
      sortType: commentsSort,
    }),
    queryFn: async (after, { signal }) =>
      queryFn(
        {
          after,
          comment: commentIdFromUrlHash,
          limit: COMMENT.LIMIT,
          repliesLimit: COMMENT.REPLIES_LIMIT,
          sort: commentsSort,
          [domainIdKey]: domainId,
        },
        { signal }
      ),
    initialData: initialComments,
    /**
     * The API takes at max 20 seconds to add a new comment to the actual
     * list, so if a user creates a new comment and tries to reload the
     * comments, it won't appear. We keep this list on cache or 20 seconds
     * so the user still sees newly added comments until the API does the
     * processing needed to actually return it on the GET comments list
     */
    cacheTime: COMMENTS_STALE_TIME,
    useErrorBoundary: true,
    getNextPageParam: (lastComments) =>
      lastComments?.length >= COMMENT.LIMIT
        ? lastComments?.at(-1)?.commentId
        : null,
    /**
     * In specific cases, the `commentPages` can be transform as a array of
     * null, because this, we filter the `commentPages` before treat the comment
     * duplicates.
     *
     * There are edge cases where comments can be duplicated from the APIs..
     * here we make sure those won't break the list and treat comments as
     * a flat list instead of dealing with an array of pages for easier
     * rendering.
     */
    select: (pages) => stripObjDuplicates(nonNull(pages), 'commentId'),
    onSuccess: (data) => {
      dispatchCommentsCount({
        type: 'populate',
        count: getCommentsCount(initialCommentsCount, data.pages.flat()),
      });
    },
  });

  useEffect(() => {
    if (!isInitialLoading && isSuccess && comments.length === 0) {
      setCommentsListEmpty(true);
      return;
    }
    setCommentsListEmpty(false);
  }, [isInitialLoading, isSuccess, comments]);

  useEffect(() => {
    let timeout;

    if (lastCommentIdFromUrlHash.current !== commentIdFromUrlHash) {
      timeout = setTimeout(() => {
        if (!isSuccess || !hashCommentRef.current) {
          return;
        }

        /**
         * TODO: fix this. There are cases where the comments is overflown,
         * scroll should also happen on parent component to make the
         * `hashCommentRef` visible
         */
        scrollToElement(hashCommentRef.current.offsetTop, isLg);
        lastCommentIdFromUrlHash.current = commentIdFromUrlHash;
      }, MILLISECONDS_TO_WAIT_BEFORE_SCROLLING_TO_COMMENT);
    }

    return () => clearTimeout(timeout);
  }, [isSuccess, commentIdFromUrlHash, isLg]);

  useIntersectionObserver({
    enabled: hasNextPage,
    target: fetchNextPageRef,
    onIntersect: () => {
      sendEvent({
        action: 'load_comments_reading',
        category: gaEventCategory,
      });
      fetchNextPage();
    },
  });

  if (isSuccess && comments.length === 0) {
    return null;
  }

  return (
    <div
      ref={commentsListComponentRef}
      className={twMerge(
        'scrollbar-light flex flex-col gap-y-2 overflow-x-hidden overflow-y-scroll p-1 pb-6',
        className
      )}
    >
      {comments.map(
        ({
          commentDate,
          commentFixed,
          commentId,
          commentLikes,
          commentStatusName,
          commentText,
          edited,
          isReview,
          parentId,
          replies,
          userId,
          userLevel,
          userName,
          userPhoto,
          userSpecialtyName,
          userTypeName,
          userUsername,
        }) => (
          <CommentListItem
            key={commentId}
            canBeRecommendedAsReview={canCommentsBeRecommendedAsReview}
            commentDate={commentDate}
            commentFixed={commentFixed}
            commentId={commentId}
            commentLikes={commentLikes}
            commentStatusName={commentStatusName}
            commentText={commentText}
            hashCommentRef={hashCommentRef}
            isDisliked={isDisliked(commentId)}
            isEdited={edited}
            isLiked={isLiked(commentId)}
            isReview={isReview}
            parentId={parentId}
            replies={replies}
            userId={userId}
            userLevel={userLevel}
            userName={userName}
            userPhoto={userPhoto}
            userSpecialtyName={userSpecialtyName}
            userTypeName={userTypeName}
            userUsername={userUsername}
          />
        )
      )}
      {(isLoading || isFetchingNextPage) && (
        <SkeletonGroup items={4} skeleton={CommentListItemSkeleton} />
      )}
      <div ref={fetchNextPageRef} />
    </div>
  );
};

CommentsList.propTypes = {
  canCommentsBeRecommendedAsReview: PropTypes.bool,
  commentsCount: PropTypes.number.isRequired,
  queryFn: PropTypes.func.isRequired,
};

export default CommentsList;
